import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Calendar, MessageCircle, Building2, ChevronDown, User, Phone, MapPin, Mail,
  Users, Plus, Pencil, X, Check, CheckCircle2, Clock, XCircle, ChevronRight,
  Headphones, BarChart3, Share2, Globe, Play, Trash2, Send, Wrench, Gift, QrCode, Download, Upload, FileText,
  Link2, Settings, FolderPlus, File, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { showIntercom, showNewMessage, showMessages, showConversation } from '../../utils/intercom';
import { QRCodeCanvas } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// QR Code base URL - separate subdomain in production, preview URL for preview
const QR_BASE_URL = BACKEND_URL?.includes('preview.emergentagent.com') 
  ? BACKEND_URL 
  : 'https://qr.gastro-beratung.de';

// Category images
const CATEGORY_IMAGES = {
  pos_payment: 'https://images.unsplash.com/photo-1715635846028-f162249377c8?w=400&h=200&fit=crop',
  reservation: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop',
  staff_planning: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=200&fit=crop',
  smart_order: 'https://images.unsplash.com/photo-1740150884358-5a254d7ae5de?w=400&h=200&fit=crop',
  delivery: 'https://images.unsplash.com/photo-1526367790999-0150786686a2?w=400&h=200&fit=crop',
  loyalty: 'https://images.unsplash.com/photo-1726666339581-07d2c51baeeb?w=400&h=200&fit=crop',
  optimization: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
  social_media: 'https://images.unsplash.com/photo-1619462729367-e47da54ba992?w=400&h=200&fit=crop',
  website_reviews: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=400&h=200&fit=crop'
};

const SERVICE_ICONS = {
  support: Headphones,
  business_analysis: BarChart3,
  social_media: Share2,
  website_management: Globe
};

const PortalPage = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [services, setServices] = useState([]);
  const [systems, setSystems] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('gastro'); // 'gastro', 'support', or 'tools'
  const [loading, setLoading] = useState(true);

  // Tools state
  const [voucherCount, setVoucherCount] = useState(10);
  const [voucherValue, setVoucherValue] = useState(50);
  const [qrUrls, setQrUrls] = useState([]);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [expandedTool, setExpandedTool] = useState(null); // 'voucher', 'qr', 'dynamic-qr' or null

  // Dynamic QR Codes state
  const [qrGroups, setQrGroups] = useState([]);
  const [selectedQrGroup, setSelectedQrGroup] = useState(null);
  const [qrGroupModalOpen, setQrGroupModalOpen] = useState(false);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [editingQrGroup, setEditingQrGroup] = useState(null);
  const [editingQrCode, setEditingQrCode] = useState(null);
  const [selectedCodeIds, setSelectedCodeIds] = useState([]);
  const [qrGroupForm, setQrGroupForm] = useState({
    name: '',
    time_windows_enabled: false,
    time_windows: [{ start_time: '09:00', end_time: '12:00', target_url: 'target_1' }, { start_time: '12:00', end_time: '22:00', target_url: 'target_2' }]
  });
  const [qrCodeForm, setQrCodeForm] = useState({
    name: '',
    target_url: '',
    target_url_2: '',
    target_type_1: 'url', // 'url' or 'pdf'
    target_type_2: 'url'  // 'url' or 'pdf'
  });
  const [bulkQrCount, setBulkQrCount] = useState(10);
  const [bulkQrUrl, setBulkQrUrl] = useState('');
  const [bulkQrUrl2, setBulkQrUrl2] = useState('');
  const [bulkTargetType1, setBulkTargetType1] = useState('url');
  const [bulkTargetType2, setBulkTargetType2] = useState('url');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [loadingQrGroups, setLoadingQrGroups] = useState(false);

  // Modals
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editLocationModalOpen, setEditLocationModalOpen] = useState(false);

  // Team data
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' });

  // Location form
  const [locationForm, setLocationForm] = useState({ name: '', contact_person: '', phone: '', address: '' });

  // Inquiry form
  const [inquiryForm, setInquiryForm] = useState({
    hasExistingSystem: false,
    existingSystemName: '',
    dissatisfactionReason: '',
    requirements: '',
    preferredContact: 'call'
  });

  useEffect(() => {
    fetchLocations();
    fetchTeamMembers();
    fetchIntercomConversations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationData();
    }
  }, [selectedLocation]);

  // Fetch Intercom conversations when switching to support tab
  useEffect(() => {
    if (activeTab === 'support') {
      fetchIntercomConversations();
    }
  }, [activeTab]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchLocationData = async () => {
    setLoading(true);
    try {
      const [servicesRes, systemsRes] = await Promise.all([
        axios.get(`${API}/locations/${selectedLocation.id}/services`),
        axios.get(`${API}/locations/${selectedLocation.id}/gastro-systems`)
      ]);
      setServices(servicesRes.data);
      setSystems(systemsRes.data);
    } catch (error) {
      console.error('Failed to fetch location data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntercomConversations = async () => {
    setConversationsLoading(true);
    try {
      const response = await axios.get(`${API}/intercom/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch Intercom conversations:', error);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const hasActiveServices = services.some(s => s.status === 'active');
  const hasActiveSystems = systems.some(s => s.status === 'active');
  const isNewUser = !hasActiveServices && !hasActiveSystems;
  
  // Check if user has active support subscription for tools access
  const supportService = services.find(s => s.service_type === 'support');
  const hasSupportSubscription = supportService?.status === 'active';
  
  // Function to request support subscription (reuses existing modal logic)
  const handleRequestSupportForTools = () => {
    if (supportService) {
      setSelectedService(supportService);
      setServiceModalOpen(true);
    }
  };

  // ============ TOOLS FUNCTIONS ============
  
  // Generate short unique ID (15 chars): timestamp base36 + random chars
  const generateShortId = () => {
    const timestamp = Date.now().toString(36); // ~8 chars
    const randomPart = Math.random().toString(36).substring(2, 9); // 7 chars
    return (timestamp + randomPart).substring(0, 15).toUpperCase();
  };

  // Tool A: Generate Lightspeed voucher CSV
  const handleGenerateVouchers = () => {
    const count = parseInt(voucherCount) || 10;
    const value = parseInt(voucherValue) || 50;
    
    let csvContent = "imported_id,balance\n";
    for (let i = 0; i < count; i++) {
      csvContent += `${generateShortId()},${value}\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lightspeed_gutscheine_${count}x${value}EUR.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Tool B: Parse CSV and extract names + URLs for QR codes
  const handleQrFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setGeneratingQr(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row, extract first URL column + first column as name (e.g. uuid)
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const columns = lines[i].split(/[;,]/).map(c => c.trim());
        const url = columns.find(c => c.startsWith('http'));
        if (url) {
          const name = columns[0] && columns[0] !== url ? columns[0] : '';
          items.push({ name, url });
        }
      }
      
      setQrUrls(items);
      setGeneratingQr(false);
    };
    
    reader.onerror = () => {
      alert('Fehler beim Lesen der Datei');
      setGeneratingQr(false);
    };
    
    reader.readAsText(file);
  };

  // Sanitize a name for use as filename
  const toFilename = (name) => name.replace(/[^\wäöüÄÖÜß-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

  // Download QR codes as ZIP (PNGs)
  const handleDownloadQrCodes = async () => {
    if (qrUrls.length === 0) return;
    
    // Dynamically import JSZip
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    // Get all QR code canvases and add to zip (named after CSV name column)
    const usedNames = {};
    for (let i = 0; i < qrUrls.length; i++) {
      const canvas = document.getElementById(`qr-canvas-${i}`);
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        const base64Data = dataUrl.split(',')[1];
        let base = toFilename(qrUrls[i].name) || `gutschein_qr_${String(i + 1).padStart(3, '0')}`;
        usedNames[base] = (usedNames[base] || 0) + 1;
        if (usedNames[base] > 1) base = `${base}_${usedNames[base]}`;
        zip.file(`${base}.png`, base64Data, { base64: true });
      }
    }
    
    // Generate and download zip
    const blob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `lightspeed_gutschein_qrcodes_${qrUrls.length}stk.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ============ DYNAMIC QR CODES FUNCTIONS ============

  // Fetch QR groups
  const fetchQrGroups = async () => {
    setLoadingQrGroups(true);
    try {
      const response = await axios.get(`${API}/qr/groups`);
      setQrGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch QR groups:', error);
    } finally {
      setLoadingQrGroups(false);
    }
  };

  // Fetch single group with codes
  const fetchQrGroup = async (groupId) => {
    try {
      const response = await axios.get(`${API}/qr/groups/${groupId}`);
      setSelectedQrGroup(response.data);
      setSelectedCodeIds([]);
    } catch (error) {
      console.error('Failed to fetch QR group:', error);
    }
  };

  // Create QR group
  const handleCreateQrGroup = async () => {
    try {
      await axios.post(`${API}/qr/groups`, qrGroupForm);
      fetchQrGroups();
      setQrGroupModalOpen(false);
      setQrGroupForm({ name: '', time_windows_enabled: false, time_windows: [{ start_time: '09:00', end_time: '12:00', target_url: 'target_1' }, { start_time: '12:00', end_time: '22:00', target_url: 'target_2' }] });
    } catch (error) {
      console.error('Failed to create QR group:', error);
      alert('Fehler beim Erstellen der Gruppe');
    }
  };

  // Update QR group
  const handleUpdateQrGroup = async () => {
    try {
      await axios.put(`${API}/qr/groups/${editingQrGroup.id}`, qrGroupForm);
      fetchQrGroups();
      if (selectedQrGroup?.id === editingQrGroup.id) {
        fetchQrGroup(editingQrGroup.id);
      }
      setQrGroupModalOpen(false);
      setEditingQrGroup(null);
    } catch (error) {
      console.error('Failed to update QR group:', error);
      alert('Fehler beim Aktualisieren der Gruppe');
    }
  };

  // Delete QR group
  const handleDeleteQrGroup = async (groupId) => {
    if (!confirm('Möchtest du diese Gruppe und alle enthaltenen QR-Codes wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/qr/groups/${groupId}`);
      setQrGroups(qrGroups.filter(g => g.id !== groupId));
      if (selectedQrGroup?.id === groupId) {
        setSelectedQrGroup(null);
      }
    } catch (error) {
      console.error('Failed to delete QR group:', error);
      alert('Fehler beim Löschen der Gruppe');
    }
  };

  // Create single QR code
  const handleCreateQrCode = async () => {
    if (!selectedQrGroup) return;
    try {
      const { name, target_url, target_url_2 } = qrCodeForm;
      await axios.post(`${API}/qr/groups/${selectedQrGroup.id}/codes`, [{ name, target_url, target_url_2: target_url_2 || null }]);
      fetchQrGroup(selectedQrGroup.id);
      setQrCodeModalOpen(false);
      setQrCodeForm({ name: '', target_url: '', target_url_2: '', target_type_1: 'url', target_type_2: 'url', pdf_name_1: '', pdf_name_2: '' });
    } catch (error) {
      console.error('Failed to create QR code:', error);
      alert('Fehler beim Erstellen des QR-Codes');
    }
  };

  // Create bulk QR codes
  const handleCreateBulkQrCodes = async () => {
    if (!selectedQrGroup || !bulkQrUrl) return;
    try {
      const codes = Array.from({ length: bulkQrCount }, (_, i) => ({
        name: `QR Code ${(selectedQrGroup.codes?.length || 0) + i + 1}`,
        target_url: bulkQrUrl,
        target_url_2: bulkQrUrl2 || null
      }));
      await axios.post(`${API}/qr/groups/${selectedQrGroup.id}/codes`, codes);
      fetchQrGroup(selectedQrGroup.id);
      setBulkQrCount(10);
      setBulkQrUrl('');
      setBulkQrUrl2('');
    } catch (error) {
      console.error('Failed to create bulk QR codes:', error);
      alert('Fehler beim Erstellen der QR-Codes');
    }
  };

  // Update QR code
  const handleUpdateQrCode = async () => {
    try {
      const { name, target_url, target_url_2 } = qrCodeForm;
      await axios.put(`${API}/qr/codes/${editingQrCode.id}`, { name, target_url, target_url_2 });
      fetchQrGroup(selectedQrGroup.id);
      setQrCodeModalOpen(false);
      setEditingQrCode(null);
    } catch (error) {
      console.error('Failed to update QR code:', error);
      alert('Fehler beim Aktualisieren des QR-Codes');
    }
  };

  // Delete QR code
  const handleDeleteQrCode = async (codeId) => {
    // Use window.confirm with a clearer approach
    const confirmed = window.confirm('Möchtest du diesen QR-Code wirklich löschen?');
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/qr/codes/${codeId}`);
      // Refresh the group data
      if (selectedQrGroup?.id) {
        fetchQrGroup(selectedQrGroup.id);
      }
    } catch (error) {
      console.error('Failed to delete QR code:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Löschen des QR-Codes';
      alert(errorMsg);
    }
  };

  // Toggle selection of a single QR code
  const toggleCodeSelection = (codeId) => {
    setSelectedCodeIds(prev => prev.includes(codeId) ? prev.filter(id => id !== codeId) : [...prev, codeId]);
  };

  // Bulk delete selected QR codes
  const handleBulkDeleteQrCodes = async () => {
    if (selectedCodeIds.length === 0) return;
    const confirmed = window.confirm(`${selectedCodeIds.length} QR-Code(s) wirklich löschen?\n\nAchtung: Bereits gedruckte oder verteilte QR-Codes funktionieren danach nicht mehr!`);
    if (!confirmed) return;
    
    try {
      const response = await axios.post(`${API}/qr/codes/bulk-delete`, { code_ids: selectedCodeIds });
      fetchQrGroup(selectedQrGroup.id);
      alert(response.data.message);
    } catch (error) {
      console.error('Failed to bulk delete QR codes:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen der QR-Codes');
    }
  };

  // Upload PDF for QR target - now accepts which target field to update
  const handlePdfUpload = async (event, targetField = 'target_url') => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/qr/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const pdfUrl = `${BACKEND_URL}${response.data.url}`;
      
      // Set the URL and original filename in the appropriate form fields
      const nameField = targetField === 'target_url' ? 'pdf_name_1' : 'pdf_name_2';
      setQrCodeForm(prev => ({ ...prev, [targetField]: pdfUrl, [nameField]: response.data.filename }));
      alert(`PDF "${response.data.filename}" hochgeladen!`);
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      alert(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingPdf(false);
      event.target.value = ''; // Reset input
    }
  };

  // Upload PDF for bulk creation
  const handleBulkPdfUpload = async (event, targetNum = 1) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/qr/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const pdfUrl = `${BACKEND_URL}${response.data.url}`;
      
      if (targetNum === 1) {
        setBulkQrUrl(pdfUrl);
      } else {
        setBulkQrUrl2(pdfUrl);
      }
      alert(`PDF "${response.data.filename}" hochgeladen!`);
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      alert(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingPdf(false);
      event.target.value = '';
    }
  };

  // Import from CSV (supports: url | url_1,url_2 | name,url_1,url_2)
  const handleDynamicQrCsvImport = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedQrGroup) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      const isUrl = (val) => /^https?:\/\//i.test(val) || (/^[^\s,]+\.[a-z]{2,}/i.test(val) && !val.includes(' '));
      
      // Detect header and column layout
      const firstCols = lines[0].split(/[;,]/).map(c => c.trim().toLowerCase());
      const hasHeader = firstCols.some(c => c.includes('url') || c.includes('name') || c.includes('link') || c.includes('ziel'));
      const startIndex = hasHeader ? 1 : 0;
      
      let nameIdx = -1, urlIdx = 0, url2Idx = 1;
      if (hasHeader) {
        nameIdx = firstCols.findIndex(c => c.includes('name') || c.includes('tisch'));
        const urlIndices = firstCols.map((c, i) => (c.includes('url') || c.includes('link') || c.includes('ziel')) ? i : -1).filter(i => i >= 0);
        urlIdx = urlIndices[0] ?? (nameIdx === 0 ? 1 : 0);
        url2Idx = urlIndices[1] ?? urlIdx + 1;
      } else {
        // No header: if first column is not a URL, treat it as name
        const probe = lines[0].split(/[;,]/).map(c => c.trim());
        if (probe.length >= 2 && !isUrl(probe[0])) {
          nameIdx = 0; urlIdx = 1; url2Idx = 2;
        }
      }
      
      const names = [];
      const urls = [];
      const urls2 = [];
      
      for (let i = startIndex; i < lines.length; i++) {
        const columns = lines[i].split(/[;,]/).map(c => c.trim());
        if (columns[urlIdx]) {
          urls.push(columns[urlIdx]);
          names.push(nameIdx >= 0 ? (columns[nameIdx] || '') : '');
          urls2.push(columns[url2Idx] || '');
        }
      }
      
      if (urls.length === 0) {
        alert('Keine URLs in der CSV gefunden');
        return;
      }
      
      try {
        await axios.post(`${API}/qr/import-csv`, {
          group_id: selectedQrGroup.id,
          urls: urls,
          urls_2: urls2.some(u => u) ? urls2 : null,
          names: names.some(n => n) ? names : null
        });
        fetchQrGroup(selectedQrGroup.id);
        alert(`${urls.length} QR-Code(s) importiert!`);
      } catch (error) {
        console.error('Failed to import CSV:', error);
        alert(error.response?.data?.detail || 'Fehler beim Import');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Label helper: which time windows are mapped to a target
  const getTargetWindowLabel = (targetKey) => {
    if (!selectedQrGroup?.time_windows_enabled) return '';
    const windows = (selectedQrGroup.time_windows || []).filter(tw => tw.target_url === targetKey);
    if (windows.length === 0) return ' (kein Zeitfenster zugeordnet)';
    return ` (${windows.map(tw => `${tw.start_time}–${tw.end_time} Uhr`).join(', ')})`;
  };

  // Open edit modal with correct target types + real PDF filenames
  const openEditQrCode = (code) => {
    const isPdf1 = code.target_url?.includes('/api/qr/files/');
    const isPdf2 = code.target_url_2?.includes('/api/qr/files/');
    setEditingQrCode(code);
    setQrCodeForm({
      name: code.name,
      target_url: code.target_url,
      target_url_2: code.target_url_2 || '',
      target_type_1: isPdf1 ? 'pdf' : 'url',
      target_type_2: isPdf2 ? 'pdf' : 'url',
      pdf_name_1: '',
      pdf_name_2: ''
    });
    setQrCodeModalOpen(true);
    const fetchPdfName = async (url, key) => {
      try {
        const fileId = url.split('/').pop();
        const res = await axios.get(`${API}/qr/files/${fileId}/info`);
        setQrCodeForm(prev => ({ ...prev, [key]: res.data.original_name }));
      } catch (e) { /* Datei-Info optional */ }
    };
    if (isPdf1) fetchPdfName(code.target_url, 'pdf_name_1');
    if (isPdf2) fetchPdfName(code.target_url_2, 'pdf_name_2');
  };

  // Export QR codes as CSV (name + QR link)
  const handleExportQrCsv = () => {
    if (!selectedQrGroup?.codes?.length) return;
    const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['name', 'link'].join(',')];
    selectedQrGroup.codes.forEach((code) => {
      rows.push([quote(code.name), quote(`${QR_BASE_URL}/?location=${selectedQrGroup.location_code}&code=${code.id}`)].join(','));
    });
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${toFilename(selectedQrGroup.name) || 'qr_codes'}_links.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Download QR codes as ZIP
  const handleDownloadDynamicQrCodes = async () => {
    if (!selectedQrGroup?.codes?.length) return;
    
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    const usedNames = {};
    for (let i = 0; i < selectedQrGroup.codes.length; i++) {
      const code = selectedQrGroup.codes[i];
      const canvas = document.getElementById(`dynamic-qr-${code.id}`);
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        const base64Data = dataUrl.split(',')[1];
        let base = toFilename(code.name) || code.id;
        usedNames[base] = (usedNames[base] || 0) + 1;
        if (usedNames[base] > 1) base = `${base}_${usedNames[base]}`;
        zip.file(`${base}.png`, base64Data, { base64: true });
      }
    }
    
    const blob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `qr_codes_${selectedQrGroup.name.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Load QR groups when tool is expanded
  useEffect(() => {
    if (expandedTool === 'dynamic-qr' && qrGroups.length === 0) {
      fetchQrGroups();
    }
  }, [expandedTool]);

  // Service toggle handler
  const handleServiceToggle = (service) => {
    if (service.status !== 'active') {
      setSelectedService(service);
      setServiceModalOpen(true);
    }
  };

  const handleRequestService = async () => {
    try {
      await axios.post(`${API}/locations/${selectedLocation.id}/services/${selectedService.service_type}/request`);
      setServiceModalOpen(false);
      fetchLocationData();
      fetchIntercomConversations(); // Refresh conversations list
      alert('Deine Anfrage wurde gesendet! Wir melden uns in Kürze bei dir.');
    } catch (error) {
      console.error('Failed to request service:', error);
    }
  };

  // Inquiry handlers
  const openInquiryModal = (system) => {
    setSelectedCategory(system);
    setInquiryForm({
      hasExistingSystem: system.status === 'active',
      existingSystemName: system.current_system_name || '',
      dissatisfactionReason: '',
      requirements: '',
      preferredContact: 'call'
    });
    setInquiryModalOpen(true);
  };

  const handleSubmitInquiry = async () => {
    try {
      await axios.post(`${API}/locations/${selectedLocation.id}/gastro-systems/${selectedCategory.category}/inquiry`, {
        category: selectedCategory.category,
        has_existing_system: inquiryForm.hasExistingSystem,
        existing_system_name: inquiryForm.existingSystemName,
        dissatisfaction_reason: inquiryForm.dissatisfactionReason,
        requirements: inquiryForm.requirements,
        preferred_contact: inquiryForm.preferredContact
      });
      setInquiryModalOpen(false);
      fetchLocationData();
      fetchIntercomConversations(); // Refresh conversations list
      alert('Deine Anfrage wurde gesendet! Wir melden uns in Kürze bei dir.');
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
    }
  };

  // Team handlers
  const handleInviteTeamMember = async () => {
    if (!inviteForm.name || !inviteForm.email) return;
    try {
      await axios.post(`${API}/team/invite`, inviteForm);
      setInviteForm({ name: '', email: '' });
      fetchTeamMembers();
      alert('Einladung gesendet!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Einladen');
    }
  };

  // Location handlers
  const handleCreateLocation = async () => {
    if (!locationForm.name) return;
    try {
      await axios.post(`${API}/locations`, locationForm);
      setLocationForm({ name: '', contact_person: '', phone: '', address: '' });
      setLocationModalOpen(false);
      fetchLocations();
    } catch (error) {
      alert('Fehler beim Erstellen');
    }
  };

  const handleUpdateLocation = async () => {
    try {
      await axios.put(`${API}/locations/${selectedLocation.id}`, locationForm);
      setEditLocationModalOpen(false);
      fetchLocations();
    } catch (error) {
      alert('Fehler beim Speichern');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (locations.length <= 1) {
      alert('Du brauchst mindestens einen Standort.');
      return;
    }
    if (!window.confirm('Möchtest du diesen Standort wirklich löschen? Alle zugehörigen Daten (Systeme, Services) werden ebenfalls gelöscht.')) {
      return;
    }
    try {
      await axios.delete(`${API}/locations/${locationId}`);
      fetchLocations();
      // If deleted location was selected, select first available
      if (selectedLocation?.id === locationId) {
        const remaining = locations.filter(l => l.id !== locationId);
        if (remaining.length > 0) {
          setSelectedLocation(remaining[0]);
        }
      }
    } catch (error) {
      alert('Fehler beim Löschen');
    }
  };

  const openEditLocationModal = () => {
    setLocationForm({
      name: selectedLocation.name || '',
      contact_person: selectedLocation.contact_person || '',
      phone: selectedLocation.phone || '',
      address: selectedLocation.address || ''
    });
    setEditLocationModalOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-none text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Aktiv
          </span>
        );
      case 'in_consultation':
      case 'pending':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-none text-sm font-medium">
            <Clock className="w-4 h-4" />
            In Beratung
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-off text-ink/60 rounded-none text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Nicht aktiv
          </span>
        );
    }
  };

  return (
    <>
      <Header />
      <main className="pb-16 min-h-screen bg-off" data-testid="portal-page">
        <div className="border-b-2 border-ink bg-lilac/40 mb-8">
          <div className="wrap max-w-6xl py-8 md:py-10">
            <p className="eyebrow mb-3"><span className="star" aria-hidden="true">✳</span>Kundenportal</p>
            <h1 className="display-2">Hallo{user?.name ? `, ${user.name.split(' ')[0]}` : ''} <span className="serif-it text-purple">– schön, dass du da bist.</span></h1>
          </div>
        </div>
        <div className="wrap max-w-6xl">

          {/* New User CTA */}
          {isNewUser && (
            <div className="bg-purple rounded-none p-6 lg:p-8 mb-8 text-white" data-testid="new-user-cta">
              <h2 className="text-xl lg:text-2xl font-bold mb-2">
                Starte jetzt mit deiner digitalen Gastro-Landschaft
              </h2>
              <p className="text-white/80 text-lg mb-6 max-w-2xl">
                Du hast noch keine Systeme eingerichtet. Lass dich von uns beraten und finde 
                die perfekten digitalen Lösungen für deinen Betrieb.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/erstberatung">
                  <Button className="btn--white px-6 font-medium">
                    <Calendar className="w-5 h-5 mr-2" />
                    Beratungstermin
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="btn--ghost-off px-6 font-medium"
                  onClick={() => setActiveTab('gastro')}
                >
                  <Building2 className="w-5 h-5 mr-2" />
                  Digitale Erstberatung
                </Button>
                <a href="https://wa.me/491787261209" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn--ghost-off px-6 font-medium">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* User Info Section */}
          <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-semibold text-ink">Deine Daten</h2>
              <Button variant="ghost" size="sm" onClick={() => setTeamModalOpen(true)} className="text-purple">
                <Users className="w-4 h-4 mr-2" />
                Team verwalten
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-ink/60" />
                <div>
                  <p className="text-base text-ink/60">Name</p>
                  <p className="text-ink text-lg">{user?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-ink/60" />
                <div>
                  <p className="text-base text-ink/60">E-Mail</p>
                  <p className="text-ink text-lg">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-ink/60" />
                <div>
                  <p className="text-base text-ink/60">Telefon</p>
                  <p className="text-ink text-lg">{user?.phone || '–'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-ink/60" />
                <div>
                  <p className="text-base text-ink/60">Gastronomie</p>
                  <p className="text-ink text-lg">{user?.company_name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Location Selection & Info */}
          <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-semibold text-ink">Standorte</h2>
              <Button variant="ghost" size="sm" onClick={() => setLocationModalOpen(true)} className="text-purple">
                <Plus className="w-4 h-4 mr-2" />
                Neuer Standort
              </Button>
            </div>

            {/* Location Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {locations.map((loc) => {
                const isSelected = selectedLocation?.id === loc.id;
                return (
                  <div
                    key={loc.id}
                    className={`relative p-4 rounded-none border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple bg-purple/5' 
                        : 'border-ink/30 hover:border-ink bg-white'
                    }`}
                    onClick={() => setSelectedLocation(loc)}
                    data-testid={`location-card-${loc.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-none flex items-center justify-center ${
                          isSelected ? 'bg-purple' : 'bg-off'
                        }`}>
                          <MapPin className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-ink/60'}`} />
                        </div>
                        <div>
                          <h3 className={`font-medium ${isSelected ? 'text-purple' : 'text-ink'}`}>
                            {loc.name}
                          </h3>
                          <p className="text-lg text-ink/60 mt-0.5">
                            {loc.address || 'Keine Adresse'}
                          </p>
                          {loc.contact_person && (
                            <p className="text-base text-ink/60 mt-1">
                              {loc.contact_person}
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-purple" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected Location Details */}
            {selectedLocation && (
              <div className="p-4 bg-off rounded-none">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-ink">Standort-Details</h3>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={openEditLocationModal} className="">
                      <Pencil className="w-4 h-4 mr-1" />
                      Bearbeiten
                    </Button>
                    {locations.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteLocation(selectedLocation.id)} 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                        data-testid="delete-location-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Löschen
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-base text-ink/60 mb-1">Ansprechpartner</p>
                    <p className="text-ink">{selectedLocation.contact_person || '–'}</p>
                  </div>
                  <div>
                    <p className="text-base text-ink/60 mb-1">Telefon</p>
                    <p className="text-ink">{selectedLocation.phone || '–'}</p>
                  </div>
                  <div>
                    <p className="text-base text-ink/60 mb-1">Adresse</p>
                    <p className="text-ink">{selectedLocation.address || '–'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Service Subscriptions */}
          <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
            <h2 className="text-lg font-semibold text-ink mb-4">Service-Angebote</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => {
                const Icon = SERVICE_ICONS[service.service_type] || Headphones;
                const isActive = service.status === 'active';
                const isInConsultation = service.status === 'in_consultation' || service.status === 'pending';
                
                return (
                  <div
                    key={service.id}
                    className={`p-4 rounded-none border ${
                      isActive 
                        ? 'border-green-200 bg-green-50' 
                        : isInConsultation 
                        ? 'border-amber-200 bg-amber-50' 
                        : 'border-ink/15 bg-white'
                    }`}
                    data-testid={`service-card-${service.service_type}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-none flex items-center justify-center flex-shrink-0 ${
                          isActive 
                            ? 'bg-green-100' 
                            : isInConsultation 
                            ? 'bg-amber-100' 
                            : 'bg-off'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            isActive 
                              ? 'text-green-600' 
                              : isInConsultation 
                              ? 'text-amber-600' 
                              : 'text-ink/60'
                          }`} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display font-bold text-ink">{service.name}</h3>
                          <p className="text-base text-ink/80 mt-1">{service.description}</p>
                          {isInConsultation && (
                            <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-amber-100 text-amber-700 rounded-none text-xs font-medium">
                              <Clock className="w-3 h-3" />
                              In Anfrage
                            </span>
                          )}
                          {isActive && (
                            <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-green-100 text-green-700 rounded-none text-xs font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Aktiv
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Toggle hidden when active, yellow when in_consultation */}
                      {!isActive && (
                        <Switch
                          checked={isInConsultation}
                          onCheckedChange={() => handleServiceToggle(service)}
                          disabled={isInConsultation}
                          className={`flex-shrink-0 ${isInConsultation ? 'data-[state=checked]:bg-amber-500' : ''}`}
                          data-testid={`service-toggle-${service.service_type}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tab Toggle */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              onClick={() => setActiveTab('gastro')}
              className={activeTab === 'gastro' ? '' : 'btn--white'}
            >
              <Building2 className="w-5 h-5 mr-2" />
              Gastro-Landschaft
            </Button>
            <Button
              onClick={() => setActiveTab('support')}
              className={activeTab === 'support' ? '' : 'btn--white'}
            >
              <Headphones className="w-5 h-5 mr-2" />
              Support
            </Button>
            <Button
              onClick={() => setActiveTab('tools')}
              className={activeTab === 'tools' ? '' : 'btn--white'}
              data-testid="tools-tab-button"
            >
              <Wrench className="w-5 h-5 mr-2" />
              Hilfreiche Werkzeuge
            </Button>
          </div>

          {/* Tools Section */}
          {activeTab === 'tools' && (
            <div className="space-y-4" data-testid="tools-section">
              {/* Premium Warning Banner */}
              {!hasSupportSubscription && (
                <div className="bg-amber-50 border border-amber-200 rounded-none p-5" data-testid="tools-premium-warning">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-none flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-800 mb-1">Support-Abo erforderlich</h3>
                      <p className="text-amber-700 text-sm mb-3">
                        Um die hilfreichen Werkzeuge nutzen zu können, benötigst du ein aktives Support-Abonnement.
                      </p>
                      <button
                        onClick={handleRequestSupportForTools}
                        className="inline-flex items-center gap-2 text-sm font-medium text-purple hover:text-deep transition-colors"
                        data-testid="request-support-for-tools-btn"
                      >
                        <Headphones className="w-4 h-4" />
                        Support-Abo anfragen
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tool A: Lightspeed Gutschein Generator */}
              <div className={`bg-white rounded-none border-2 border-ink overflow-hidden ${!hasSupportSubscription ? 'opacity-60' : ''}`}>
                {/* Header - Always visible */}
                <button
                  onClick={() => hasSupportSubscription ? setExpandedTool(expandedTool === 'voucher' ? null : 'voucher') : handleRequestSupportForTools()}
                  className="w-full p-5 flex items-center justify-between hover:bg-off transition-colors"
                  data-testid="voucher-tool-toggle"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${hasSupportSubscription ? 'bg-purple/10' : 'bg-off'} rounded-none flex items-center justify-center flex-shrink-0`}>
                      <Gift className={`w-6 h-6 ${hasSupportSubscription ? 'text-purple' : 'text-ink/60'}`} />
                    </div>
                    <div className="text-left">
                      <h3 className={`text-lg font-bold ${hasSupportSubscription ? 'text-ink' : 'text-ink/60'}`}>Lightspeed Gutschein Generator</h3>
                      <p className="text-ink/80 text-sm">Generiere Gutscheine für den Import im Lightspeed System.</p>
                    </div>
                  </div>
                  {hasSupportSubscription ? (
                    <ChevronDown className={`w-5 h-5 text-ink/60 transition-transform ${expandedTool === 'voucher' ? 'rotate-180' : ''}`} />
                  ) : (
                    <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-none">Premium</span>
                  )}
                </button>

                {/* Expandable Content */}
                {expandedTool === 'voucher' && hasSupportSubscription && (
                  <div className="px-5 pb-5 border-t border-ink/15">
                    {/* Instructions */}
                    <div className="bg-off rounded-none p-4 my-4">
                      <h4 className="font-semibold text-ink mb-3">Anleitung</h4>
                      <ol className="list-decimal list-inside space-y-2 text-ink/80 text-sm">
                        <li>Generiere unten eine beliebige Anzahl Gutscheine.</li>
                        <li>Lade die Gutschein-Datei herunter.</li>
                        <li>Besuche die <a href="https://pos-admin.lsk.lightspeed.app/gift-card/card" target="_blank" rel="noopener noreferrer" className="text-purple hover:underline font-medium">Seite für Geschenkkarten im Lightspeed Backoffice</a>.</li>
                        <li>Klicke auf „Geschenkkarten importieren".</li>
                        <li>Wähle einen eindeutigen Namen, bei „Ursprung der Karten" wähle „Andere Option".</li>
                        <li>Starte den Importvorgang.</li>
                        <li>Wähle den Tab „Serien" im Geschenkkarten-Menü.</li>
                        <li>Lade die fertigen Gutscheine über den Download Button herunter.</li>
                        <li>Auf Wunsch nutze unser Tool „<button onClick={() => setExpandedTool('qr')} className="text-purple hover:underline font-medium">Lightspeed Gutschein QR-Code Generator</button>" um für die Gutscheine QR-Codes zu erzeugen.</li>
                      </ol>
                    </div>

                    {/* Form */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <Label htmlFor="voucher-count">Anzahl Gutscheine</Label>
                        <Input
                          id="voucher-count"
                          type="number"
                          min="1"
                          max="1000"
                          value={voucherCount}
                          onChange={(e) => setVoucherCount(e.target.value)}
                          className="mt-1"
                          data-testid="voucher-count-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="voucher-value">Wert pro Gutschein (€)</Label>
                        <Input
                          id="voucher-value"
                          type="number"
                          min="1"
                          max="10000"
                          value={voucherValue}
                          onChange={(e) => setVoucherValue(e.target.value)}
                          className="mt-1"
                          data-testid="voucher-value-input"
                        />
                      </div>
                      <Button
                        onClick={handleGenerateVouchers}
                        className=""
                        data-testid="generate-vouchers-button"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        CSV Generieren & Herunterladen
                      </Button>
                    </div>

                    <p className="text-sm text-ink/60 mt-3">
                      Es werden {voucherCount || 0} Gutscheine à {voucherValue || 0}€ generiert (Gesamtwert: {(parseInt(voucherCount) || 0) * (parseInt(voucherValue) || 0)}€)
                    </p>
                  </div>
                )}
              </div>

              {/* Tool B: Lightspeed Gutschein QR-Code Generator */}
              <div id="qr-tool" className={`bg-white rounded-none border-2 border-ink overflow-hidden ${!hasSupportSubscription ? 'opacity-60' : ''}`}>
                {/* Header - Always visible */}
                <button
                  onClick={() => hasSupportSubscription ? setExpandedTool(expandedTool === 'qr' ? null : 'qr') : handleRequestSupportForTools()}
                  className="w-full p-5 flex items-center justify-between hover:bg-off transition-colors"
                  data-testid="qr-tool-toggle"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${hasSupportSubscription ? 'bg-purple/10' : 'bg-off'} rounded-none flex items-center justify-center flex-shrink-0`}>
                      <QrCode className={`w-6 h-6 ${hasSupportSubscription ? 'text-purple' : 'text-ink/60'}`} />
                    </div>
                    <div className="text-left">
                      <h3 className={`text-lg font-bold ${hasSupportSubscription ? 'text-ink' : 'text-ink/60'}`}>Lightspeed Gutschein QR-Code Generator</h3>
                      <p className="text-ink/80 text-sm">Generiere QR-Codes für bereits im Lightspeed System hinterlegte Gutscheine.</p>
                    </div>
                  </div>
                  {hasSupportSubscription ? (
                    <ChevronDown className={`w-5 h-5 text-ink/60 transition-transform ${expandedTool === 'qr' ? 'rotate-180' : ''}`} />
                  ) : (
                    <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-none">Premium</span>
                  )}
                </button>

                {/* Expandable Content */}
                {expandedTool === 'qr' && hasSupportSubscription && (
                  <div className="px-5 pb-5 border-t border-ink/15">
                    {/* Instructions */}
                    <div className="bg-off rounded-none p-4 my-4">
                      <h4 className="font-semibold text-ink mb-3">Anleitung</h4>
                      <ol className="list-decimal list-inside space-y-2 text-ink/80 text-sm">
                        <li>Nutze unser Tool „<button onClick={() => setExpandedTool('voucher')} className="text-purple hover:underline font-medium">Lightspeed Gutschein Generator</button>" um Gutscheine zu erzeugen.</li>
                        <li>Lade die Gutschein-Datei herunter - die QR Codes enthalten den im Lightspeed System definierten Wert und sind sofort am POS einsatzbereit.</li>
                      </ol>
                    </div>

                    {/* Upload */}
                    <div className="border-2 border-dashed border-ink/30 rounded-none p-6 text-center mb-4">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleQrFileUpload}
                        className="hidden"
                        id="qr-csv-upload"
                        data-testid="qr-csv-upload"
                      />
                      <label htmlFor="qr-csv-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-ink/60 mx-auto mb-3" />
                        <p className="text-ink/80 font-medium">CSV-Datei aus Lightspeed hochladen</p>
                        <p className="text-sm text-ink/60 mt-1">Datei mit uuid, batch, url Spalten auswählen</p>
                      </label>
                    </div>

                    {generatingQr && (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto"></div>
                        <p className="text-ink/80 mt-2">QR-Codes werden generiert...</p>
                      </div>
                    )}

                    {qrUrls.length > 0 && !generatingQr && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-ink/80 font-medium">{qrUrls.length} QR-Codes generiert</p>
                          <Button
                            onClick={handleDownloadQrCodes}
                            className=""
                            data-testid="download-qr-codes-button"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Als ZIP herunterladen
                          </Button>
                        </div>

                        {/* QR Code Preview Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-96 overflow-y-auto p-2 bg-off rounded-none">
                          {qrUrls.map((item, index) => (
                            <div key={index} className="bg-white p-3 rounded-none text-center">
                              <QRCodeCanvas
                                id={`qr-canvas-${index}`}
                                value={item.url}
                                size={120}
                                level="M"
                                includeMargin={true}
                              />
                              <p className="text-xs text-ink/60 mt-1 truncate" title={item.name || `#${index + 1}`}>
                                {item.name || `#${index + 1}`}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tool C: Dynamic QR Codes */}
              <div className={`bg-white rounded-none border-2 border-ink overflow-hidden ${!hasSupportSubscription ? 'opacity-60' : ''}`}>
                {/* Header - Always visible */}
                <button
                  onClick={() => {
                    if (hasSupportSubscription) {
                      setExpandedTool(expandedTool === 'dynamic-qr' ? null : 'dynamic-qr');
                    } else {
                      handleRequestSupportForTools();
                    }
                  }}
                  className="w-full p-5 flex items-center justify-between hover:bg-off transition-colors"
                  data-testid="dynamic-qr-tool-toggle"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${hasSupportSubscription ? 'bg-purple/10' : 'bg-off'} rounded-none flex items-center justify-center flex-shrink-0`}>
                      <Link2 className={`w-6 h-6 ${hasSupportSubscription ? 'text-purple' : 'text-ink/60'}`} />
                    </div>
                    <div className="text-left">
                      <h3 className={`text-lg font-bold ${hasSupportSubscription ? 'text-ink' : 'text-ink/60'}`}>Dynamische QR Codes</h3>
                      <p className="text-ink/80 text-sm">Generiere für eine digitale Speisekarte sowie einen oder mehrere Bestell-Links QR Codes.</p>
                    </div>
                  </div>
                  {hasSupportSubscription ? (
                    <ChevronDown className={`w-5 h-5 text-ink/60 transition-transform ${expandedTool === 'dynamic-qr' ? 'rotate-180' : ''}`} />
                  ) : (
                    <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-none">Premium</span>
                  )}
                </button>

                {/* Expandable Content */}
                {expandedTool === 'dynamic-qr' && hasSupportSubscription && (
                  <div className="px-5 pb-5 border-t border-ink/15">
                    {loadingQrGroups ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto"></div>
                        <p className="text-ink/80 mt-2">Lade Gruppen...</p>
                      </div>
                    ) : selectedQrGroup ? (
                      /* Group Detail View */
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Button
                              onClick={() => setSelectedQrGroup(null)}
                              variant="ghost"
                              className="p-2"
                            >
                              <ChevronRight className="w-5 h-5 rotate-180" />
                            </Button>
                            <div>
                              <h4 className="font-bold text-ink">{selectedQrGroup.name}</h4>
                              <p className="text-sm text-ink/60">{selectedQrGroup.codes?.length || 0} QR-Codes</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                setEditingQrGroup(selectedQrGroup);
                                setQrGroupForm({
                                  name: selectedQrGroup.name,
                                  time_windows_enabled: selectedQrGroup.time_windows_enabled,
                                  time_windows: (selectedQrGroup.time_windows?.length > 0 ? selectedQrGroup.time_windows : [{ start_time: '09:00', end_time: '12:00', target_url: 'target_1' }, { start_time: '12:00', end_time: '22:00', target_url: 'target_2' }]).map(tw => ({ ...tw }))
                                });
                                setQrGroupModalOpen(true);
                              }}
                              variant="outline"
                              size="sm"
                            >
                              <Settings className="w-4 h-4 mr-1" /> Einstellungen
                            </Button>
                            {selectedQrGroup.codes?.length > 0 && (
                              <>
                                <Button
                                  onClick={handleExportQrCsv}
                                  variant="outline"
                                  size="sm"
                                  data-testid="export-qr-csv-button"
                                >
                                  <FileText className="w-4 h-4 mr-1" /> CSV Export
                                </Button>
                                <Button
                                  onClick={() => handleDownloadDynamicQrCodes()}
                                  className="bg-purple hover:bg-deep text-white"
                                  size="sm"
                                >
                                  <Download className="w-4 h-4 mr-1" /> Download ZIP
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Time Window Info */}
                        {selectedQrGroup.time_windows_enabled && (
                          <div className="bg-amber-50 border border-amber-200 rounded-none p-3 mb-4">
                            <div className="flex items-center gap-2 text-amber-800 text-sm font-medium mb-2">
                              <Clock className="w-4 h-4" /> Zeitfenster aktiv
                            </div>
                            <div className="text-xs text-amber-700 space-y-1">
                              {selectedQrGroup.time_windows?.map((tw, i) => (
                                <div key={i}>
                                  {tw.start_time} - {tw.end_time} Uhr → {tw.target_url === 'target_1' ? 'Ziel 1' : tw.target_url === 'target_2' ? 'Ziel 2' : tw.target_url}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Add QR Codes Section */}
                        <div className="bg-off rounded-none p-4 mb-4">
                          <h5 className="font-semibold text-ink mb-3">QR-Codes hinzufügen</h5>
                          
                          {/* Bulk Create */}
                          <div className="space-y-3 mb-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs">Anzahl</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={bulkQrCount}
                                  onChange={(e) => setBulkQrCount(parseInt(e.target.value) || 1)}
                                  className="mt-1"
                                />
                              </div>
                              
                              {/* Target 1 */}
                              <div className="md:col-span-2">
                                <Label className="text-xs">Ziel{selectedQrGroup.time_windows_enabled ? ` 1${getTargetWindowLabel('target_1')}` : ''}</Label>
                                <div className="flex gap-2 mt-1">
                                  <select
                                    value={bulkTargetType1}
                                    onChange={(e) => { setBulkTargetType1(e.target.value); setBulkQrUrl(''); }}
                                    className="px-2 py-2 border rounded-none text-sm bg-white"
                                  >
                                    <option value="url">URL</option>
                                    <option value="pdf">PDF</option>
                                  </select>
                                  {bulkTargetType1 === 'url' ? (
                                    <Input
                                      type="text"
                                      placeholder="https://..."
                                      value={bulkQrUrl}
                                      onChange={(e) => setBulkQrUrl(e.target.value)}
                                      className="flex-1"
                                    />
                                  ) : (
                                    <div className="flex-1 flex items-center gap-2">
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => handleBulkPdfUpload(e, 1)}
                                        className="hidden"
                                        id="bulk-pdf-1"
                                      />
                                      <Button
                                        onClick={() => document.getElementById('bulk-pdf-1').click()}
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        disabled={uploadingPdf}
                                      >
                                        {bulkQrUrl ? '✓ PDF gewählt' : 'PDF wählen'}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Target 2 (only if time windows) */}
                            {selectedQrGroup.time_windows_enabled && (
                              <div>
                                <Label className="text-xs">Ziel 2{getTargetWindowLabel('target_2')}</Label>
                                <div className="flex gap-2 mt-1">
                                  <select
                                    value={bulkTargetType2}
                                    onChange={(e) => { setBulkTargetType2(e.target.value); setBulkQrUrl2(''); }}
                                    className="px-2 py-2 border rounded-none text-sm bg-white"
                                  >
                                    <option value="url">URL</option>
                                    <option value="pdf">PDF</option>
                                  </select>
                                  {bulkTargetType2 === 'url' ? (
                                    <Input
                                      type="text"
                                      placeholder="https://..."
                                      value={bulkQrUrl2}
                                      onChange={(e) => setBulkQrUrl2(e.target.value)}
                                      className="flex-1"
                                    />
                                  ) : (
                                    <div className="flex-1 flex items-center gap-2">
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => handleBulkPdfUpload(e, 2)}
                                        className="hidden"
                                        id="bulk-pdf-2"
                                      />
                                      <Button
                                        onClick={() => document.getElementById('bulk-pdf-2').click()}
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        disabled={uploadingPdf}
                                      >
                                        {bulkQrUrl2 ? '✓ PDF gewählt' : 'PDF wählen'}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              onClick={handleCreateBulkQrCodes}
                              disabled={!bulkQrUrl || uploadingPdf}
                              className=""
                              size="sm"
                            >
                              <Plus className="w-4 h-4 mr-1" /> {bulkQrCount} QR-Codes erstellen
                            </Button>
                            <div className="relative">
                              <input
                                type="file"
                                accept=".csv"
                                onChange={handleDynamicQrCsvImport}
                                className="hidden"
                                id="csv-import-dynamic"
                              />
                              <Button
                                onClick={() => document.getElementById('csv-import-dynamic').click()}
                                variant="outline"
                                size="sm"
                                data-testid="qr-csv-import-button"
                              >
                                <Upload className="w-4 h-4 mr-1" /> CSV Import
                              </Button>
                            </div>
                            <Button
                              onClick={() => {
                                setEditingQrCode(null);
                                setQrCodeForm({ name: '', target_url: '', target_url_2: '', target_type_1: 'url', target_type_2: 'url' });
                                setQrCodeModalOpen(true);
                              }}
                              variant="outline"
                              size="sm"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Einzeln erstellen
                            </Button>
                          </div>
                          <p className="text-xs text-ink/60 mt-2" data-testid="qr-csv-format-hint">
                            CSV-Format: <code className="bg-off px-1 rounded-none">name,url_1,url_2</code> – „name" (z.B. Tischname) und „url_2" (Zeitfenster 2) sind optional.{' '}
                            <a href="/beispiel_qr_import_momo_ramen.csv" download className="text-purple hover:underline">Beispiel-CSV herunterladen</a>
                          </p>
                        </div>

                        {/* Selection toolbar */}
                        {selectedQrGroup.codes?.length > 0 && (
                          <div className="flex items-center justify-between mb-3 p-2 bg-off rounded-none">
                            <label className="flex items-center gap-2 text-sm text-ink/80 cursor-pointer">
                              <input
                                type="checkbox"
                                data-testid="select-all-qr-codes"
                                checked={selectedCodeIds.length === selectedQrGroup.codes.length && selectedCodeIds.length > 0}
                                onChange={(e) => setSelectedCodeIds(e.target.checked ? selectedQrGroup.codes.map(c => c.id) : [])}
                                className="w-4 h-4 accent-purple"
                              />
                              Alle auswählen
                              {selectedCodeIds.length > 0 && (
                                <span className="text-ink/60" data-testid="selected-codes-count">({selectedCodeIds.length} ausgewählt)</span>
                              )}
                            </label>
                            {selectedCodeIds.length > 0 && (
                              <Button
                                onClick={handleBulkDeleteQrCodes}
                                size="sm"
                                className="btn--danger"
                                data-testid="bulk-delete-qr-codes-button"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> {selectedCodeIds.length} löschen
                              </Button>
                            )}
                          </div>
                        )}

                        {/* QR Codes Grid */}
                        {selectedQrGroup.codes?.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-[500px] overflow-y-auto">
                            {selectedQrGroup.codes.map((code) => (
                              <div key={code.id} className={`bg-white border rounded-none p-3 text-center group relative ${selectedCodeIds.includes(code.id) ? 'border-purple ring-1 ring-purple' : 'border-ink/15'}`}>
                                <input
                                  type="checkbox"
                                  data-testid={`select-qr-code-${code.id}`}
                                  checked={selectedCodeIds.includes(code.id)}
                                  onChange={() => toggleCodeSelection(code.id)}
                                  className="absolute top-1.5 left-1.5 w-4 h-4 accent-purple cursor-pointer z-10"
                                />
                                <QRCodeCanvas
                                  id={`dynamic-qr-${code.id}`}
                                  value={`${QR_BASE_URL}/?location=${selectedQrGroup.location_code}&code=${code.id}`}
                                  size={100}
                                  level="M"
                                  includeMargin={true}
                                />
                                <p className="text-xs font-medium text-ink/80 mt-2 truncate" data-testid={`qr-code-name-${code.id}`}>{code.name}</p>
                                <p className="text-xs text-ink/60 truncate">
                                  {code.target_url?.includes('/api/qr/files/')
                                    ? 'PDF-Datei'
                                    : code.target_url?.length > 25 ? `${code.target_url.substring(0, 25)}...` : code.target_url}
                                </p>
                                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                  <button
                                    onClick={() => openEditQrCode(code)}
                                    className="p-1 bg-white rounded-none hover:bg-off"
                                    data-testid={`edit-qr-code-${code.id}`}
                                  >
                                    <Pencil className="w-3 h-3 text-ink/80" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQrCode(code.id)}
                                    className="p-1 bg-white rounded-none hover:bg-red-100"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-500" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-ink/60">
                            <QrCode className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Noch keine QR-Codes in dieser Gruppe</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Groups List View */
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-ink">Deine QR-Code Gruppen</h4>
                          <Button
                            onClick={() => {
                              setEditingQrGroup(null);
                              setQrGroupForm({ name: '', time_windows_enabled: false, time_windows: [{ start_time: '09:00', end_time: '12:00', target_url: 'target_1' }, { start_time: '12:00', end_time: '22:00', target_url: 'target_2' }] });
                              setQrGroupModalOpen(true);
                            }}
                            className="bg-purple hover:bg-deep text-white"
                            size="sm"
                          >
                            <FolderPlus className="w-4 h-4 mr-1" /> Neue Gruppe
                          </Button>
                        </div>

                        {qrGroups.length > 0 ? (
                          <div className="space-y-2">
                            {qrGroups.map((group) => (
                              <div
                                key={group.id}
                                className="flex items-center justify-between p-3 bg-off rounded-none hover:bg-off transition-colors"
                              >
                                <button
                                  onClick={() => fetchQrGroup(group.id)}
                                  className="flex items-center gap-3 flex-1 text-left"
                                >
                                  <div className="w-10 h-10 bg-purple/10 rounded-none flex items-center justify-center">
                                    <QrCode className="w-5 h-5 text-purple" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-ink">{group.name}</p>
                                    <p className="text-xs text-ink/60">
                                      {group.code_count || 0} QR-Codes
                                      {group.time_windows_enabled && ' • Zeitfenster aktiv'}
                                    </p>
                                  </div>
                                </button>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => {
                                      setEditingQrGroup(group);
                                      setQrGroupForm({
                                        name: group.name,
                                        time_windows_enabled: group.time_windows_enabled,
                                        time_windows: (group.time_windows?.length > 0 ? group.time_windows : [{ start_time: '09:00', end_time: '12:00', target_url: 'target_1' }, { start_time: '12:00', end_time: '22:00', target_url: 'target_2' }]).map(tw => ({ ...tw }))
                                      });
                                      setQrGroupModalOpen(true);
                                    }}
                                    className="p-2 hover:bg-lilac/50 rounded-none"
                                  >
                                    <Pencil className="w-4 h-4 text-ink/60" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQrGroup(group.id)}
                                    className="p-2 hover:bg-red-100 rounded-none"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-ink/60">
                            <FolderPlus className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Noch keine Gruppen erstellt</p>
                            <p className="text-sm">Erstelle eine Gruppe um QR-Codes zu organisieren</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QR Group Modal */}
          {qrGroupModalOpen && (
            <div className="fixed inset-0 bg-ink/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-lg w-full p-6">
                <h3 className="text-xl font-bold text-ink mb-4">
                  {editingQrGroup ? 'Gruppe bearbeiten' : 'Neue Gruppe erstellen'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Gruppenname</Label>
                    <Input
                      value={qrGroupForm.name}
                      onChange={(e) => setQrGroupForm({ ...qrGroupForm, name: e.target.value })}
                      placeholder="z.B. Tische 1-30"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-off rounded-none">
                    <div>
                      <p className="font-medium text-ink">Zeitfenster aktivieren</p>
                      <p className="text-sm text-ink/60">Links leiten je nach Uhrzeit auf verschiedene Ziele</p>
                    </div>
                    <Switch
                      checked={qrGroupForm.time_windows_enabled}
                      onCheckedChange={(checked) => setQrGroupForm({ ...qrGroupForm, time_windows_enabled: checked })}
                    />
                  </div>
                  
                  {qrGroupForm.time_windows_enabled && (
                    <div className="space-y-3">
                      <Label>Zeitfenster konfigurieren</Label>
                      {qrGroupForm.time_windows.map((tw, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-off rounded-none">
                          <Input
                            type="time"
                            value={tw.start_time}
                            onChange={(e) => {
                              const newWindows = qrGroupForm.time_windows.map((w, i) => i === idx ? { ...w, start_time: e.target.value } : w);
                              setQrGroupForm({ ...qrGroupForm, time_windows: newWindows });
                            }}
                            className="w-28"
                          />
                          <span className="text-ink/60">bis</span>
                          <Input
                            type="time"
                            value={tw.end_time}
                            onChange={(e) => {
                              const newWindows = qrGroupForm.time_windows.map((w, i) => i === idx ? { ...w, end_time: e.target.value } : w);
                              setQrGroupForm({ ...qrGroupForm, time_windows: newWindows });
                            }}
                            className="w-28"
                          />
                          <span className="text-ink/60">→</span>
                          <select
                            value={tw.target_url}
                            onChange={(e) => {
                              const newWindows = qrGroupForm.time_windows.map((w, i) => i === idx ? { ...w, target_url: e.target.value } : w);
                              setQrGroupForm({ ...qrGroupForm, time_windows: newWindows });
                            }}
                            className="flex-1 p-2 border rounded-none text-sm"
                          >
                            <option value="target_1">Ziel 1</option>
                            <option value="target_2">Ziel 2</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => {
                      setQrGroupModalOpen(false);
                      setEditingQrGroup(null);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={editingQrGroup ? handleUpdateQrGroup : handleCreateQrGroup}
                    disabled={!qrGroupForm.name}
                    className="flex-1"
                  >
                    {editingQrGroup ? 'Speichern' : 'Erstellen'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* QR Code Modal */}
          {qrCodeModalOpen && (
            <div className="fixed inset-0 bg-ink/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-bold text-ink mb-4">
                  {editingQrCode ? 'QR-Code bearbeiten' : 'Neuer QR-Code'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={qrCodeForm.name}
                      onChange={(e) => setQrCodeForm({ ...qrCodeForm, name: e.target.value })}
                      placeholder="z.B. Tisch 1"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Target 1 */}
                  <div className="border rounded-none p-4 bg-off">
                    <Label className="mb-2 block font-semibold">
                      Ziel{selectedQrGroup?.time_windows_enabled ? ` 1${getTargetWindowLabel('target_1')}` : ''}
                    </Label>
                    
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => setQrCodeForm({ ...qrCodeForm, target_type_1: 'url', target_url: '' })}
                        className={`flex-1 py-2 px-3 rounded-none text-sm font-medium transition-colors ${
                          qrCodeForm.target_type_1 === 'url' 
                            ? 'bg-purple text-white' 
                            : 'bg-white border-2 border-ink text-ink/80 hover:bg-off'
                        }`}
                      >
                        <Link2 className="w-4 h-4 inline mr-1" /> URL
                      </button>
                      <button
                        onClick={() => setQrCodeForm({ ...qrCodeForm, target_type_1: 'pdf', target_url: '' })}
                        className={`flex-1 py-2 px-3 rounded-none text-sm font-medium transition-colors ${
                          qrCodeForm.target_type_1 === 'pdf' 
                            ? 'bg-purple text-white' 
                            : 'bg-white border-2 border-ink text-ink/80 hover:bg-off'
                        }`}
                      >
                        <File className="w-4 h-4 inline mr-1" /> PDF
                      </button>
                    </div>
                    
                    {qrCodeForm.target_type_1 === 'url' ? (
                      <Input
                        value={qrCodeForm.target_url}
                        onChange={(e) => setQrCodeForm({ ...qrCodeForm, target_url: e.target.value })}
                        placeholder="https://..."
                      />
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handlePdfUpload(e, 'target_url')}
                          className="hidden"
                          id="pdf-upload-target1"
                        />
                        <Button
                          onClick={() => document.getElementById('pdf-upload-target1').click()}
                          variant="outline"
                          className="w-full"
                          disabled={uploadingPdf}
                        >
                          {uploadingPdf ? (
                            <span className="animate-spin mr-2">⏳</span>
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {qrCodeForm.target_url ? 'PDF ersetzen' : 'PDF auswählen (max. 5MB)'}
                        </Button>
                        {qrCodeForm.target_url && (
                          <p className="text-xs text-green-600 mt-1 truncate" data-testid="pdf-name-target1">
                            ✓ {qrCodeForm.pdf_name_1 || 'PDF-Datei hinterlegt'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Target 2 (only if time windows enabled) */}
                  {selectedQrGroup?.time_windows_enabled && (
                    <div className="border rounded-none p-4 bg-off">
                      <Label className="mb-2 block font-semibold">Ziel 2{getTargetWindowLabel('target_2')}</Label>
                      
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setQrCodeForm({ ...qrCodeForm, target_type_2: 'url', target_url_2: '' })}
                          className={`flex-1 py-2 px-3 rounded-none text-sm font-medium transition-colors ${
                            qrCodeForm.target_type_2 === 'url' 
                              ? 'bg-purple text-white' 
                              : 'bg-white border-2 border-ink text-ink/80 hover:bg-off'
                          }`}
                        >
                          <Link2 className="w-4 h-4 inline mr-1" /> URL
                        </button>
                        <button
                          onClick={() => setQrCodeForm({ ...qrCodeForm, target_type_2: 'pdf', target_url_2: '' })}
                          className={`flex-1 py-2 px-3 rounded-none text-sm font-medium transition-colors ${
                            qrCodeForm.target_type_2 === 'pdf' 
                              ? 'bg-purple text-white' 
                              : 'bg-white border-2 border-ink text-ink/80 hover:bg-off'
                          }`}
                        >
                          <File className="w-4 h-4 inline mr-1" /> PDF
                        </button>
                      </div>
                      
                      {qrCodeForm.target_type_2 === 'url' ? (
                        <Input
                          value={qrCodeForm.target_url_2}
                          onChange={(e) => setQrCodeForm({ ...qrCodeForm, target_url_2: e.target.value })}
                          placeholder="https://..."
                        />
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => handlePdfUpload(e, 'target_url_2')}
                            className="hidden"
                            id="pdf-upload-target2"
                          />
                          <Button
                            onClick={() => document.getElementById('pdf-upload-target2').click()}
                            variant="outline"
                            className="w-full"
                            disabled={uploadingPdf}
                          >
                            {uploadingPdf ? (
                              <span className="animate-spin mr-2">⏳</span>
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {qrCodeForm.target_url_2 ? 'PDF ersetzen' : 'PDF auswählen (max. 5MB)'}
                          </Button>
                          {qrCodeForm.target_url_2 && (
                            <p className="text-xs text-green-600 mt-1 truncate" data-testid="pdf-name-target2">
                              ✓ {qrCodeForm.pdf_name_2 || 'PDF-Datei hinterlegt'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => {
                      setQrCodeModalOpen(false);
                      setEditingQrCode(null);
                      setQrCodeForm({ name: '', target_url: '', target_url_2: '', target_type_1: 'url', target_type_2: 'url' });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={editingQrCode ? handleUpdateQrCode : handleCreateQrCode}
                    disabled={!qrCodeForm.target_url || uploadingPdf}
                    className="flex-1"
                  >
                    {editingQrCode ? 'Speichern' : 'Erstellen'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Gastro Landscape */}
          {activeTab === 'gastro' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="gastro-landscape">
              {systems.map((system) => (
                <div
                  key={system.id}
                  className={`relative rounded-none overflow-hidden transition-all bg-white flex flex-col ${
                    system.status === 'active'
                      ? 'ring-4 ring-purple '
                      : system.status === 'in_consultation'
                      ? 'ring-2 ring-amber-400'
                      : 'border-2 border-ink'
                  }`}
                  data-testid={`system-card-${system.category}`}
                >
                  {/* Image with overlay */}
                  <div className="relative h-28 overflow-hidden flex-shrink-0">
                    <img src={CATEGORY_IMAGES[system.category]} alt={system.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-purple/60"></div>
                    <div className="absolute top-3 right-3">{getStatusBadge(system.status)}</div>
                  </div>

                  {/* Content */}
                  <div className={`p-4 flex flex-col flex-grow ${system.status === 'active' ? 'bg-purple/5' : 'bg-white'}`}>
                    <h3 className="font-bold text-ink mb-1">{system.title}</h3>
                    <p className="text-ink/80 text-lg mb-3">{system.subtitle}</p>

                    <ul className="space-y-1 mb-4">
                      {system.benefits?.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-lg text-ink/80">
                          <CheckCircle2 className="w-4 h-4 text-purple mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>

                    {/* Spacer to push button to bottom */}
                    <div className="flex-grow"></div>

                    {system.status === 'active' && system.current_system_name && (
                      <div className="mb-3 p-2 bg-green-50 rounded-none border border-green-100">
                        <p className="text-base text-green-700">Aktuelles System:</p>
                        <p className="text-lg text-green-800 font-medium">{system.current_system_name}</p>
                      </div>
                    )}

                    <Button
                      onClick={() => openInquiryModal(system)}
                      className={`w-full rounded-none ${
                        system.status === 'active'
                          ? 'bg-white text-purple border-2 border-purple hover:bg-purple/5'
                          : 'bg-purple text-white hover:bg-deep'
                      }`}
                    >
                      {system.status === 'active' ? 'Wechsel anfragen' : 'System anfragen'}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div data-testid="support-section">
              {/* New Support Request CTA */}
              <div className="bg-purple rounded-none p-6 mb-6 text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Brauchst du Hilfe?</h3>
                    <p className="text-white/80">
                      Unser Support-Team steht dir zur Verfügung. Starte einen Chat und wir helfen dir gerne weiter.
                    </p>
                  </div>
                  <Button 
                    onClick={() => showNewMessage('Hallo, ich habe eine Frage zu ')}
                    className="bg-white text-purple hover:bg-white/90 shrink-0"
                    data-testid="open-intercom-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Neue Nachricht
                  </Button>
                </div>
              </div>

              {!hasActiveServices && (
                <div className="bg-amber-50 border border-amber-200 rounded-none p-6 mb-6">
                  <h3 className="font-semibold text-amber-800 mb-2">Tipp: Service-Abo aktivieren</h3>
                  <p className="text-amber-700 mb-4">
                    Mit einem aktiven Service-Abo erhältst du priorisierten Support und Zugang zu erweiterten Funktionen.
                  </p>
                  <Button onClick={() => setActiveTab('gastro')} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                    <Building2 className="w-4 h-4 mr-2" />
                    Service-Abos ansehen
                  </Button>
                </div>
              )}

              <div className="bg-white rounded-none border-2 border-ink overflow-hidden">
                <div className="p-4 border-b border-ink/15 flex items-center justify-between">
                  <h3 className="font-semibold text-ink">Deine Anfragen & Konversationen</h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => showMessages()}
                    className="text-purple hover:text-purple/80"
                  >
                    Alle Nachrichten
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                {conversationsLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto"></div>
                    <p className="text-ink/60 mt-4">Lade Konversationen...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-ink/60 mx-auto mb-4" />
                    <p className="text-ink/60 mb-4">Noch keine Anfragen vorhanden</p>
                    <Button 
                      onClick={() => showNewMessage()}
                      className="bg-purple hover:bg-purple/90 text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Erste Nachricht senden
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-ink/15">
                    {conversations.map((conv) => (
                      <button 
                        key={conv.id} 
                        onClick={() => {
                          // Try to open specific conversation, fallback to messages list
                          if (window.Intercom) {
                            window.Intercom('showConversation', conv.intercom_conversation_id || conv.id);
                          }
                        }}
                        className="w-full p-4 flex items-center justify-between hover:bg-off transition-colors cursor-pointer text-left"
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-none flex items-center justify-center bg-purple/10">
                            <MessageCircle className="w-5 h-5 text-purple" />
                          </div>
                          <div>
                            <p className="font-medium text-ink">{conv.subject || 'Support-Anfrage'}</p>
                            <p className="text-lg text-ink/60">
                              {conv.created_at ? new Date(conv.created_at).toLocaleDateString('de-DE') : ''} - {conv.intercom_conversation_id || conv.id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-none text-xs font-medium ${
                            conv.state === 'open' ? 'bg-amber-100 text-amber-700' :
                            conv.state === 'closed' ? 'bg-green-100 text-green-700' :
                            'bg-lilac text-ink'
                          }`}>
                            {conv.state === 'open' ? 'Offen' :
                             conv.state === 'closed' ? 'Gelöst' : 'In Bearbeitung'}
                          </span>
                          <ChevronRight className="w-5 h-5 text-ink/60" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Team Modal */}
      {teamModalOpen && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Teamverwaltung</h2>
              <button onClick={() => setTeamModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {/* Invite Form */}
              <div className="mb-6 p-4 bg-off rounded-none">
                <h3 className="font-medium text-ink mb-3">Teammitglied einladen</h3>
                <div className="space-y-3">
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Name"
                    className="h-10"
                  />
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="E-Mail"
                    className="h-10"
                  />
                  <Button onClick={handleInviteTeamMember} className="w-full">
                    Einladung senden
                  </Button>
                </div>
              </div>
              {/* Team List */}
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-off rounded-none">
                    <div>
                      <p className="font-medium text-ink">{member.name}</p>
                      <p className="text-lg text-ink/60">{member.email}</p>
                    </div>
                    <span className="text-base text-ink/60">{member.role === 'owner' ? 'Inhaber' : 'Team'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Location Modal */}
      {locationModalOpen && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Neuer Standort</h2>
              <button onClick={() => setLocationModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Name des Standorts *</Label>
                <Input value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Ansprechpartner</Label>
                <Input value={locationForm.contact_person} onChange={(e) => setLocationForm({ ...locationForm, contact_person: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Telefon</Label>
                <Input value={locationForm.phone} onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Adresse</Label>
                <Input value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} className="h-11" />
              </div>
            </div>
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setLocationModalOpen(false)} className="flex-1">Abbrechen</Button>
              <Button onClick={handleCreateLocation} className="flex-1">Anlegen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Location Modal */}
      {editLocationModalOpen && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Standort bearbeiten</h2>
              <button onClick={() => setEditLocationModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Name des Standorts *</Label>
                <Input value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Ansprechpartner</Label>
                <Input value={locationForm.contact_person} onChange={(e) => setLocationForm({ ...locationForm, contact_person: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Telefon</Label>
                <Input value={locationForm.phone} onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })} className="h-11" />
              </div>
              <div>
                <Label className="text-lg text-ink/80 mb-1.5 block">Adresse</Label>
                <Input value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} className="h-11" />
              </div>
            </div>
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setEditLocationModalOpen(false)} className="flex-1">Abbrechen</Button>
              <Button onClick={handleUpdateLocation} className="flex-1">Speichern</Button>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {serviceModalOpen && selectedService && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Service aktivieren</h2>
              <button onClick={() => setServiceModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-purple/5 p-4 rounded-none mb-4">
                <h3 className="font-semibold text-purple">{selectedService.name}</h3>
                <p className="text-lg text-ink/80 mt-1">{selectedService.description}</p>
              </div>
              <p className="text-ink/80 mb-6">
                Mit dem Klick auf "Anfrage senden" erstellen wir ein Ticket und kontaktieren dich, 
                um die Details zu deinem Service-Vertrag zu besprechen.
              </p>
            </div>
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setServiceModalOpen(false)} className="flex-1">Abbrechen</Button>
              <Button onClick={handleRequestService} className="flex-1">Anfrage senden</Button>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Modal */}
      {inquiryModalOpen && selectedCategory && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">
                {selectedCategory.status === 'active' ? 'Systemwechsel anfragen' : 'System anfragen'}
              </h2>
              <button onClick={() => setInquiryModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-purple/5 p-4 rounded-none">
                <p className="font-semibold text-purple">{selectedCategory.title}</p>
                <p className="text-lg text-ink/80">{selectedCategory.subtitle}</p>
              </div>

              {selectedCategory.status !== 'active' && (
                <div>
                  <Label className="text-lg text-ink/80 mb-2 block">Hast du derzeit schon ein System?</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={!inquiryForm.hasExistingSystem} onChange={() => setInquiryForm({ ...inquiryForm, hasExistingSystem: false })} className="w-4 h-4" />
                      <span>Nein</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={inquiryForm.hasExistingSystem} onChange={() => setInquiryForm({ ...inquiryForm, hasExistingSystem: true })} className="w-4 h-4" />
                      <span>Ja</span>
                    </label>
                  </div>
                </div>
              )}

              {(inquiryForm.hasExistingSystem || selectedCategory.status === 'active') ? (
                <>
                  <div>
                    <Label className="text-lg text-ink/80 mb-1.5 block">Welches System nutzt du?</Label>
                    <Input value={inquiryForm.existingSystemName} onChange={(e) => setInquiryForm({ ...inquiryForm, existingSystemName: e.target.value })} placeholder="z.B. orderbird" className="h-11" />
                  </div>
                  <div>
                    <Label className="text-lg text-ink/80 mb-1.5 block">Warum möchtest du wechseln?</Label>
                    <Textarea value={inquiryForm.dissatisfactionReason} onChange={(e) => setInquiryForm({ ...inquiryForm, dissatisfactionReason: e.target.value })} placeholder="Deine Herausforderungen..." className="min-h-[100px]" />
                  </div>
                </>
              ) : (
                <div>
                  <Label className="text-lg text-ink/80 mb-1.5 block">Was ist dir wichtig?</Label>
                  <Textarea value={inquiryForm.requirements} onChange={(e) => setInquiryForm({ ...inquiryForm, requirements: e.target.value })} placeholder="Deine Anforderungen..." className="min-h-[100px]" />
                </div>
              )}

              <div>
                <Label className="text-lg text-ink/80 mb-2 block">Wie soll es weitergehen?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setInquiryForm({ ...inquiryForm, preferredContact: 'appointment' })}
                    className={`p-4 rounded-none border-2 text-left ${inquiryForm.preferredContact === 'appointment' ? 'border-purple bg-purple/5' : 'border-ink/15'}`}
                  >
                    <Calendar className={`w-5 h-5 mb-2 ${inquiryForm.preferredContact === 'appointment' ? 'text-purple' : 'text-ink/60'}`} />
                    <p className="font-medium text-sm">Termin</p>
                  </button>
                  <button
                    onClick={() => setInquiryForm({ ...inquiryForm, preferredContact: 'call' })}
                    className={`p-4 rounded-none border-2 text-left ${inquiryForm.preferredContact === 'call' ? 'border-purple bg-purple/5' : 'border-ink/15'}`}
                  >
                    <Phone className={`w-5 h-5 mb-2 ${inquiryForm.preferredContact === 'call' ? 'text-purple' : 'text-ink/60'}`} />
                    <p className="font-medium text-sm">Rückruf</p>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setInquiryModalOpen(false)} className="flex-1">Abbrechen</Button>
              <Button onClick={handleSubmitInquiry} className="flex-1">Anfrage senden</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PortalPage;
